// ══════════════════════════════════════════════════════════════════════════════
// push.js — Web Push notifications (Firebase Cloud Messaging)
//
// `firebase/messaging` jest importowany LENIWIE (dynamic import) — nie chcemy
// zwiększać initial bundle dla userów, których przeglądarka i tak nie wspiera
// Push API (np. iOS Safari poza trybem standalone).
// ══════════════════════════════════════════════════════════════════════════════

import { app } from "../firebase.js";
import { getPushSub, savePushSub, listMyPlayers } from "../storage.js";

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;
const SW_URL = "/firebase-messaging-sw.js";
const LOCAL_TOKEN_KEY = "fl_push_token"; // token tego urządzenia — do wyłączenia bez ponownego getToken()
const LOCAL_PLAYERS_KEY = "fl_push_players"; // playerIds ostatnio wysłane do Firestore — patrz needsPlayerIdSync()

// Reguła dopuszcza `devices.size() <= 20`. Trzymamy się niżej, bo przekroczenie
// nie obcina listy — odrzuca CAŁY zapis (patrz przycinanie w registerPushToken).
const MAX_DEVICES = 10;

export function isIOS() {
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || navigator.standalone === true;
}

// Push API dostępne w ogóle ORAZ (nie-iOS LUB iOS zainstalowany na ekran główny).
// iOS Safari w zwykłej karcie przeglądarki NIE MA Push API (iOS 16.4+ wspiera
// je tylko dla zainstalowanych PWA) — window.PushManager samo w sobie
// wystarczyłoby jako feature-detect, ale sprawdzamy standalone explicite, żeby
// nie pokazywać UI zachęcającego do włączenia powiadomień w martwej ścieżce.
//
// Świadomie NIE wołamy tu `isSupported()` z firebase/messaging, choć kuszy.
// Ta funkcja ma udokumentowane fałszywe negatywy właśnie na iOS (firebase-js-sdk
// #7734: zwracała „unsupported" w zainstalowanej PWA, w której push działał),
// a sprawdza dokładnie to samo co warunek wyżej: serviceWorker, PushManager
// i Notification. Dołożenie jej mogłoby więc wyłączyć push na jedynej
// platformie, na której jest najtrudniej go włączyć — bez żadnego zysku.
export function isPushEligible() {
  if (typeof window === "undefined") return false;
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || typeof Notification === "undefined") return false;
  if (isIOS() && !isStandalone()) return false;
  return true;
}

// Rejestruje SW + pobiera token FCM + zapisuje/scala do /push_subs/{uid}.
//
// `uid` zostaje w sygnaturze, choć storage.js bierze go teraz z sesji: wołający
// (App.jsx) i tak nim dysponuje, a jawny argument pilnuje, żeby subskrypcja nie
// powstała przypadkiem dla nie tego konta.
// Zakłada że Notification.permission === "granted" już w momencie wywołania
// (permission trzeba wywalczyć przez natywny prompt PRZED tym wywołaniem —
// patrz miękki prompt w App.jsx).
export async function registerPushToken(uid, { role, playerIds }) {
  if (!isPushEligible() || Notification.permission !== "granted") return null;
  if (!VAPID_KEY) {
    console.warn("[FL] push: brak VITE_FIREBASE_VAPID_KEY — pomijam rejestrację tokena");
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register(SW_URL);
    const { getMessaging, getToken } = await import("firebase/messaging");
    const messaging = getMessaging(app);
    const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
    if (!token) return null;

    const existing = (await getPushSub()) || { uid, role, playerIds: [], devices: [] };
    const poprzedni = (existing.devices || []).find(d => d.token === token);
    const devices = (existing.devices || []).filter(d => d.token !== token);
    // `createdAt` MUSI przeżyć ponowną rejestrację — inaczej odświeżenie tokena
    // resetowałoby datę i nie dałoby się odróżnić urządzenia dodanego dziś od
    // takiego, które jest z nami od pół roku. `lastSeenAt` odwrotnie: to
    // znacznik ostatniego kontaktu i ma się odświeżać. Ta sama zasada co
    // w upsertDailyEntry (playerActions.js).
    devices.push({
      token,
      platform: "web",
      ua: navigator.userAgent,
      createdAt: poprzedni?.createdAt ?? Date.now(),
      lastSeenAt: Date.now(),
    });

    // Scalamy z tym, co już jest w Firestore — rodzic z dwoma dziećmi rejestruje
    // push raz na dziecko (osobny ekran, osobne wywołanie z playerIds=[tegoDziecka]);
    // nadpisanie całej listy by przy drugim dziecku wywalało pierwsze z subskrypcji.
    // Przycinamy do dzieci, które rodzic WCIĄŻ ma — bez tego lista tylko rośnie,
    // a `playerIds.size() <= 10` w regułach odrzuciłby cały zapis (token włącznie)
    // po przekroczeniu limitu, gdy konto przez lata skasowało/dodało sporo dzieci.
    // `listMyPlayers()` (zapytanie do Firestore), NIE `getMyPlayerIds()` (lokalny
    // cache) — to drugie na urządzeniu, które jeszcze nie zdążyło zsynchronizować
    // dziecka dodanego na INNYM urządzeniu, przycinałoby je jako "nieistniejące"
    // i realnie odpinało od powiadomień o raportach i streaku angielskiego.
    const mojeDzieci = await listMyPlayers();
    const mergedPlayerIds = [...new Set([...(existing.playerIds || []), ...(playerIds || [])])]
      .filter(id => mojeDzieci.includes(id));
    // Sufit `devices.size() <= 20` w regułach jest twardy: przekroczenie nie
    // obcina listy, tylko odrzuca CAŁY zapis — razem ze świeżo zdobytym
    // tokenem, i po cichu, bo odmowa ląduje w catch na końcu tej funkcji.
    // Do limitu da się realnie dojechać: każda zmiana konta na tym urządzeniu
    // dokłada wpis, bo discardLocalPushToken unieważnia stary token po stronie
    // FCM, ale wpisu w devices[] POPRZEDNIEGO konta nie rusza (i nie może —
    // reguła pozwala pisać tylko własny dokument). Tamten sprząta dopiero
    // pierwsza nieudana wysyłka w sendPushToUser, czyli może poczekać tygodnie.
    // Trzymamy MAX_DEVICES najświeższych — świeży token ma lastSeenAt = teraz,
    // więc nigdy nie wypada.
    const najswiezsze = devices
      .sort((a, b) => (b.lastSeenAt || 0) - (a.lastSeenAt || 0))
      .slice(0, MAX_DEVICES);

    const sub = { ...existing, uid, role, playerIds: mergedPlayerIds, devices: najswiezsze };
    await savePushSub(sub);
    localStorage.setItem(LOCAL_TOKEN_KEY, token);
    localStorage.setItem(LOCAL_PLAYERS_KEY, JSON.stringify(mergedPlayerIds));
    console.log("[FL] push: token zarejestrowany");
    return token;
  } catch (e) {
    console.warn("[FL] push: rejestracja tokena nie powiodła się:", e.message);
    return null;
  }
}

// Wyłącza powiadomienia na TYM urządzeniu — usuwa token z devices[] w Firestore
// (serwer przestaje do niego wysyłać) i unieważnia go po stronie FCM. Nie da
// się cofnąć natywnej zgody Notification.permission przez JS (brak takiego
// API w przeglądarkach) — to jest jedyny realny sposób na "wyłącz" z poziomu apki.
export async function unsubscribePush(uid) {
  const token = localStorage.getItem(LOCAL_TOKEN_KEY);
  if (!token) return true;
  try {
    const existing = await getPushSub();
    if (existing) {
      const devices = (existing.devices || []).filter(d => d.token !== token);
      await savePushSub({ ...existing, devices });
    }
    try {
      const { getMessaging, deleteToken } = await import("firebase/messaging");
      await deleteToken(getMessaging(app));
    } catch { /* token mógł już wygasnąć — nieistotne, ważne że zniknął z Firestore */ }
    localStorage.removeItem(LOCAL_TOKEN_KEY);
    localStorage.removeItem(LOCAL_PLAYERS_KEY);
    return true;
  } catch (e) {
    console.warn("[FL] push: wyłączenie nie powiodło się:", e.message);
    return false;
  }
}

// Zrywa powiązanie urządzenia z subskrypcją push POPRZEDNIEGO właściciela.
// Nie da się posprzątać /push_subs/{stary_uid} — reguła pozwala pisać wyłącznie
// dokument pod własnym uid, a jesteśmy już zalogowani jako ktoś inny. Unieważniamy
// więc sam token po stronie FCM: wpis w devices[] starego konta zostaje, ale jest
// martwy i nic już na to urządzenie nie dostarczy.
export async function discardLocalPushToken() {
  const token = localStorage.getItem(LOCAL_TOKEN_KEY);
  localStorage.removeItem(LOCAL_TOKEN_KEY);
  localStorage.removeItem(LOCAL_PLAYERS_KEY);
  if (!token || !isPushEligible()) return;
  try {
    const { getMessaging, deleteToken } = await import("firebase/messaging");
    await deleteToken(getMessaging(app));
  } catch { /* token mógł już wygasnąć — lokalnie i tak go nie ma */ }
}

// Heurystyka stanu "powiadomienia włączone NA TYM urządzeniu" dla UI ustawień —
// natywna zgoda przeglądarki nadal obowiązuje ORAZ mamy zarejestrowany token.
export function isPushOnThisDevice() {
  return typeof Notification !== "undefined"
    && Notification.permission === "granted"
    && !!localStorage.getItem(LOCAL_TOKEN_KEY);
}

// ── Odświeżenie tokena przy starcie ─────────────────────────────────────────
// Token FCM nie jest wieczny: przeglądarka potrafi go wymienić (rotacja
// subskrypcji push, wyczyszczenie danych witryny, przeinstalowanie PWA),
// a firebase/messaging NIE robi tego sam — trzeba zawołać getToken().
//
// Do tej pory wołaliśmy je wyłącznie przy banerze, przy przełączniku w Profilu
// i przy zmianie playerIds. Czyli praktycznie nigdy po pierwszym włączeniu.
// Skutek zmierzony na produkcji 2026-10-20: token zarejestrowany 11 sierpnia
// był martwy, a nikt się nie dowiedział, bo realne wyzwalacze (raport trenera,
// wygasająca seria) trafiają się raz na tygodnie. Aplikacje natywne odświeżają
// token przy każdym uruchomieniu — to jest dokładnie to samo, tylko na webie.
//
// Dławik 24 h, bo każde odświeżenie to zapis do Firestore. Rotacja tokena
// oznacza więc najwyżej dobę ciszy, a nie kilka tygodni.
//
// ── Druga rola tej funkcji: ODTWARZANIE utraconej subskrypcji ────────────────
// Bramką jest `Notification.permission`, a NIE isPushOnThisDevice(). To cała
// różnica między „odświeżam token, który mam" a „odtwarzam subskrypcję, której
// nie mam", i to na niej funkcja wcześniej przestawała działać.
//
// isPushOnThisDevice() wymaga `fl_push_token` w localStorage, więc gate na nim
// wychodził dokładnie w tym stanie, który trzeba naprawić: zgoda przeglądarki
// nadal obowiązuje, a token lokalnie zniknął. A znika rutynowo:
//   • wylogowanie i wejście na INNE konto → ensureCacheOwner woła
//     discardLocalPushToken() i wipeLocalCache(), a `fl_push_token` nie jest
//     w DEVICE_KEYS, więc leci razem z cache'em konta;
//   • wyczyszczenie danych witryny i przeinstalowanie PWA;
//   • eksmisja storage'u po dłuższej nieaktywności (robi to iOS).
// Efekt był zawsze ten sam: przełącznik w Profilu wracał na „Włącz" i NIC nie
// potrafiło go odbudować bez ponownego kliknięcia użytkownika — bo jedyne
// wywołania getToken() siedziały za tym samym warunkiem.
//
// Zgody odwołać przez JS nie można, więc „granted" jest trwalsze niż cokolwiek
// w localStorage i jest właściwym źródłem prawdy o tym, czy wolno nam
// zarejestrować token ponownie.
const LOCAL_REFRESHED_KEY = "fl_push_refreshed_at";
const REFRESH_EVERY_MS = 24 * 60 * 60 * 1000;

export async function refreshPushTokenIfEnabled(uid, { role, playerIds } = {}) {
  if (!uid || !isPushEligible()) return;
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;

  // Dławik obowiązuje WYŁĄCZNIE wtedy, gdy token lokalnie jest. Przy jego
  // braku czekanie do doby byłoby czekaniem z martwą subskrypcją w ręku —
  // a to jest właśnie przypadek, dla którego ta gałąź istnieje.
  let lokalnyToken = null;
  try { lokalnyToken = localStorage.getItem(LOCAL_TOKEN_KEY); } catch { /* prywatny tryb */ }
  if (lokalnyToken) {
    let ostatnie = 0;
    try { ostatnie = +(localStorage.getItem(LOCAL_REFRESHED_KEY) || 0); } catch { /* jw. */ }
    if (Date.now() - ostatnie < REFRESH_EVERY_MS) return;
  }

  // Pusta lista NIE może nadpisać tego, co już jest w Firestore: rodzic na
  // ekranie wyboru zawodnika nie zna jeszcze childId, a englishStreakReminder
  // iteruje właśnie po playerIds. Sięgamy po ostatnio wysłaną listę z
  // localStorage — zero odczytów w chmurze.
  let znane = playerIds;
  if (!znane?.length) {
    try { znane = JSON.parse(localStorage.getItem(LOCAL_PLAYERS_KEY) || "[]"); } catch { znane = []; }
  }

  const token = await registerPushToken(uid, { role, playerIds: znane });
  if (token) {
    try { localStorage.setItem(LOCAL_REFRESHED_KEY, String(Date.now())); } catch { /* jw. */ }
  }
}

// Czy subskrypcja w Firestore ma nieaktualne playerIds dla tego zawodnika.
// Powód: miękki baner w App.jsx potrafi zarejestrować push na ekranie wyboru
// zawodnika, gdy childId jest jeszcze null — wtedy playerIds leci puste, a
// englishStreakReminder iteruje po playerIds i nigdy nikogo nie znajdzie.
// Porównanie idzie po localStorage, nie po Firestore — zero odczytów w chmurze
// w typowym przypadku (nic się nie zmieniło).
export function needsPlayerIdSync(playerId) {
  if (!playerId || !isPushOnThisDevice()) return false;
  try {
    const stored = JSON.parse(localStorage.getItem(LOCAL_PLAYERS_KEY) || "[]");
    return !Array.isArray(stored) || !stored.includes(playerId);
  } catch {
    return true;
  }
}

// Wywoływane po kliknięciu "Włącz" w miękkim promptcie — dopiero TU wołamy
// natywny Notification.requestPermission(), nigdy automatycznie przy starcie.
export async function requestPushPermissionAndRegister(uid, opts) {
  if (!isPushEligible()) return false;
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return false;
  const token = await registerPushToken(uid, opts);
  return !!token;
}

// Foreground — appka aktywna na pierwszym planie. W tym stanie natywna
// notyfikacja by się i tak nie pokazała w większości przeglądarek, więc
// wołający renderuje custom toast (np. reużywając istniejący flash()).
export async function initForegroundToasts(onMessageCb) {
  if (!isPushEligible()) return () => {};
  try {
    const { getMessaging, onMessage } = await import("firebase/messaging");
    const messaging = getMessaging(app);
    return onMessage(messaging, payload => {
      const title = payload.notification?.title || payload.data?.title || "FootLog";
      const body  = payload.notification?.body  || payload.data?.body  || "";
      const url   = payload.data?.url || "/";
      onMessageCb({ title, body, url });
    });
  } catch (e) {
    console.warn("[FL] push: initForegroundToasts błąd:", e.message);
    return () => {};
  }
}
